<template>
  <div class="h-full flex flex-col bg-[#f8fafc] gap-4 overflow-auto pr-2">
    <div class="print-hide bg-white rounded-xl shadow-sm border border-slate-200 overflow-visible border-t-4 border-t-[#0d4169]">
      <div class="px-6 pt-5 pb-3">
        <h2 class="text-base font-bold text-[#0d4169]">Saved Report</h2>
      </div>

      <form @submit.prevent="generateReport" class="px-6 pb-4 pt-2">
        <div class="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-2">
          <div class="flex flex-col gap-1 text-left">
            <label class="text-[0.78rem] font-semibold text-[#1e293b]">Start Date <span class="text-red-500">*</span></label>
            <select v-model="filters.startDate" required class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer">
              <option value="" disabled>Select Start Date</option>
              <option v-for="d in startDateOptions" :key="d" :value="d">{{ d }}</option>
            </select>
          </div>

          <div class="flex flex-col gap-1 text-left">
            <label class="text-[0.78rem] font-semibold text-[#1e293b]">End Date</label>
            <select v-model="filters.endDate" class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer">
              <option value="">Select End Date (Optional)</option>
              <option v-for="d in endDateOptions" :key="d" :value="d">{{ d }}</option>
            </select>
          </div>

          <div class="flex flex-col gap-1 text-left">
            <label class="text-[0.78rem] font-semibold text-[#1e293b]">Category</label>
            <select v-model="filters.categoryId" class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer">
              <option :value="null">All Categories</option>
              <option
                v-for="c in sortedCategoryOptions"
                :key="c.id"
                :value="c.id === 'product' ? 'product' : String(c.id)"
              >{{ c.name }}</option>
            </select>
          </div>

          <div class="flex flex-col gap-1 text-left">
            <label class="text-[0.78rem] font-semibold text-[#1e293b]">Item Name</label>
            <input
              v-model="filters.query"
              type="text"
              placeholder="Search by code or name..."
              class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm"
            />
          </div>
        </div>

        <div class="mt-3 pt-3 pr-8 border-t border-slate-100 flex justify-between items-center gap-3">
          <div v-if="loading" class="text-xs text-slate-500">Loading...</div>
          <button
            type="button"
            @click="handlePrintReport"
            class="px-4 py-1.5 bg-slate-200 text-slate-700 text-sm font-bold rounded-md shadow-md hover:bg-slate-300 transition-all active:scale-95 disabled:opacity-60 flex items-center gap-2"
            :disabled="loading"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M6 9V2h12v7"/>
              <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/>
              <rect x="6" y="14" width="12" height="8"/>
            </svg>
            <span>Print</span>
          </button>
          <button
            type="submit"
            class="px-5 py-1.5 bg-[#0d4169] text-white text-sm font-bold rounded-md shadow-md hover:bg-[#0a3354] transition-all active:scale-95 disabled:opacity-60"
            :disabled="loading || !filters.startDate"
          >
            <div v-if="loading" class="w-4 h-4 border-2 border-white/70 border-t-transparent rounded-full animate-spin"></div>
            <span v-else>Generate </span> 
          </button>
        </div>
      </form>
    </div>

    <div class="w-full overflow-x-auto shadow-sm rounded-xl pa-1 print-container saved-table-wrap">
      <div class="hidden print:block mb-6">
        <div class="print-doc-header print-doc-header-grid">
          <div class="print-doc-left">
            <div class="print-company-block">
              <img
                v-if="printCompanyLogo"
                :src="printCompanyLogo"
                :alt="printCompanyName || 'Company Logo'"
                class="print-company-logo"
              />
              <div v-else class="print-company-logo-fallback">Logo</div>
              <div class="print-company-text">
                <div class="print-company-name">{{ printCompanyName }}</div>
              </div>
            </div>
          </div>
          <h1 class="print-doc-title text-xl font-bold text-center mb-2">Saved Report</h1>
          <div class="print-doc-meta print-doc-right">
            <div><strong>Date:</strong> {{ new Date().toLocaleDateString() }}</div>
          </div>
        </div>
        <hr class="my-4 border-black" />
      </div>

      <table class="w-full border-collapse bg-white overflow-hidden print-table">
        <thead>
          <tr>
            <th rowspan="2" class="px-2 py-1 text-left font-bold text-[#64748b] tracking-wider">No.</th>
            <th rowspan="2" @click="sortBy('itemCode')" class="px-2 py-1 text-left font-bold text-[#64748b] tracking-wider cursor-pointer">
              Item Code <span v-if="sortKey === 'itemCode'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
            </th>
            <th rowspan="2" @click="sortBy('itemName')" class="px-2 py-1 text-left font-bold text-[#64748b] tracking-wider cursor-pointer">
              Item Name <span v-if="sortKey === 'itemName'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
            </th>
            <th rowspan="2" @click="sortBy('category')" class="px-2 py-1 text-left font-bold text-[#64748b] tracking-wider cursor-pointer">
              Category <span v-if="sortKey === 'category'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
            </th>
            <th rowspan="2" @click="sortBy('unit')" class="px-2 py-1 text-left font-bold text-[#64748b] tracking-wider cursor-pointer">
              Unit <span v-if="sortKey === 'unit'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
            </th>
            <th rowspan="2" @click="sortBy('minStock')" class="px-2 py-1 text-left font-bold text-[#64748b] tracking-wider cursor-pointer">
              Min Level <span v-if="sortKey === 'minStock'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
            </th>
            <th rowspan="2" @click="sortBy('initialBalance')" class="px-2 py-1 text-left font-bold text-[#64748b] tracking-wider cursor-pointer">
              Initial Balance <span v-if="sortKey === 'initialBalance'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
            </th>
            <th rowspan="2" @click="sortBy('grv')" class="px-2 py-1 text-left font-bold text-[#64748b] tracking-wider cursor-pointer">
              GRV <span v-if="sortKey === 'grv'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
            </th>
            <th rowspan="2" @click="sortBy('siv')" class="px-2 py-1 text-left font-bold text-[#64748b] tracking-wider cursor-pointer">
              SIV <span v-if="sortKey === 'siv'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
            </th>
            <th rowspan="2" @click="sortBy('return')" class="px-2 py-1 text-left font-bold text-[#64748b] tracking-wider cursor-pointer">
              Return <span v-if="sortKey === 'return'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
            </th>
            <th colspan="2" class="px-2 py-1 text-center font-bold text-[#64748b] tracking-wider">Adjustment</th>
            <th rowspan="2" @click="sortBy('current_balance')" class="px-2 py-1 text-left font-bold text-[#64748b] tracking-wider cursor-pointer">
              Current Balance <span v-if="sortKey === 'current_balance'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
            </th>
            <th rowspan="2" @click="sortBy('totalPrice')" class="px-2 py-1 text-left font-bold text-[#64748b] tracking-wider cursor-pointer">
              Total Price <span v-if="sortKey === 'totalPrice'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
            </th>
          </tr>
          <tr>
            <th @click="sortBy('adjustmentPlus')" class="px-2 py-1 text-left font-bold text-[#64748b] tracking-wider cursor-pointer">
              + <span v-if="sortKey === 'adjustmentPlus'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
            </th>
            <th @click="sortBy('adjustmentMinus')" class="px-2 py-1 text-left font-bold text-[#64748b] tracking-wider cursor-pointer">
              - <span v-if="sortKey === 'adjustmentMinus'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, index) in sortedRows" :key="row.id" class="bg-white border-b">
            <td class="px-2 py-1 text-slate-700">{{ index + 1 }}</td>
            <td class="px-2 py-1 text-slate-700">{{ row.itemCode }}</td>
            <td class="px-2 py-1" :class="row.rowType === 'product' ? 'text-green-600 font-semibold' : 'text-slate-700'">{{ row.itemName }}</td>
            <td class="px-2 py-1 text-slate-700">{{ row.category?.name || '-' }}</td>
            <td class="px-2 py-1 text-slate-700">{{ row.unit }}</td>
            <td class="px-2 py-1 text-slate-700">{{ formatQty(row.minStock) }}</td>
            <td class="px-2 py-1 text-slate-700">{{ formatQty(row.initialBalance) }}</td>
            <td class="px-2 py-1 text-slate-700">{{ formatQty(row.grv) }}</td>
            <td class="px-2 py-1 text-slate-700">{{ formatQty(row.siv) }}</td>
            <td class="px-2 py-1 text-slate-700">{{ formatQty(row.return) }}</td>
            <td class="px-2 py-1 text-emerald-700 font-semibold">{{ formatMoney(row.adjustmentPlus) }}</td>
            <td class="px-2 py-1 text-red-600 font-semibold">{{ formatMoney(row.adjustmentMinus) }}</td>
            <td class="px-2 py-1 text-slate-700">
              <span
                class="px-2 py-0.5 rounded font-bold border"
                :class="(row.current_balance || 0) > 0 ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-600 border-red-200'"
              >
                {{ formatQty(row.current_balance) }}
              </span>
            </td>
            <td class="px-2 py-1 text-slate-700 font-semibold">{{ formatMoney(row.totalPrice) }}</td>
          </tr>
          <tr v-if="sortedRows.length === 0">
            <td colspan="15" class="px-4 py-10 text-center text-slate-400 italic text-sm">No saved report found for selected filters.</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import store from '../../../store'
import { apiFetch } from '../../../utils/api'

const categories = ref([])
const startDateOptions = ref([])
const endDateOptions = ref([])
const rows = ref([])
const loading = ref(false)

const filters = ref({
  startDate: '',
  endDate: '',
  categoryId: null,
  query: ''
})

const sortKey = ref('itemName')
const sortOrder = ref('asc')

const currentUser = computed(() => store.getters.currentUser)
const companyId = computed(() => currentUser.value?.companyId || currentUser.value?.company_id || null)
const branchId = computed(() => currentUser.value?.branchId || currentUser.value?.branch_id || null)
const printCompanyName = computed(() =>
  currentUser.value?.company?.name ||
  currentUser.value?.companyName ||
  currentUser.value?.company_name ||
  'Company'
)
const printCompanyLogo = computed(() =>
  currentUser.value?.company?.logo_url ||
  currentUser.value?.companyLogo ||
  currentUser.value?.company_logo ||
  ''
)

// Categories + Product, sorted A–Z by name (All / placeholder stays separate in the template).
const sortedCategoryOptions = computed(() => {
  const options = (Array.isArray(categories.value) ? categories.value : []).map(c => ({
    id: c.id,
    name: String(c.name || '')
  }))
  options.push({ id: 'product', name: 'Product' })
  return options.sort((a, b) =>
    a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
  )
})

function formatQty(value) {
  const n = Number(value || 0)
  if (!Number.isFinite(n)) return '0'
  return (Math.round(n * 1000) / 1000).toString()
}

function formatMoney(value) {
  const n = Number(value || 0)
  if (!Number.isFinite(n)) return '0.00'
  return n.toFixed(2)
}

async function loadCategories() {
  if (!companyId.value && !branchId.value) return
  try {
    const query = branchId.value ? `branchId=${branchId.value}` : `companyId=${companyId.value}`
    const res = await apiFetch(`/categories?${query}`)
    if (res.ok) categories.value = await res.json()
  } catch (err) {
    console.error('Failed to load categories', err)
  }
}

async function loadDateOptions() {
  try {
    const res = await apiFetch('/saved-balance-report/dates')
    if (!res.ok) return
    const data = await res.json().catch(() => ({}))
    startDateOptions.value = Array.isArray(data?.startDates) ? data.startDates : []
    endDateOptions.value = Array.isArray(data?.endDates) ? data.endDates : []
  } catch (err) {
    console.error('Failed to load saved report date options', err)
  }
}

async function generateReport() {
  if (!filters.value.startDate) {
    alert('Start date is required')
    return
  }
  loading.value = true
  try {
    const params = new URLSearchParams()
    params.set('startDate', filters.value.startDate)
    if (filters.value.endDate) params.set('endDate', filters.value.endDate)
    if (filters.value.categoryId) params.set('categoryId', String(filters.value.categoryId))
    if (filters.value.query && filters.value.query.trim()) params.set('query', filters.value.query.trim())

    const res = await apiFetch(`/saved-balance-report?${params.toString()}`)
    if (!res.ok) {
      rows.value = []
      return
    }
    const data = await res.json().catch(() => [])
    rows.value = Array.isArray(data) ? data : []
  } catch (err) {
    console.error('Failed to fetch saved reports', err)
    rows.value = []
  } finally {
    loading.value = false
  }
}

function sortBy(key) {
  if (sortKey.value === key) sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc'
  else {
    sortKey.value = key
    sortOrder.value = 'asc'
  }
}

const sortedRows = computed(() => {
  const list = [...rows.value]
  const modifier = sortOrder.value === 'asc' ? 1 : -1
  const getVal = (row) => {
    if (sortKey.value === 'category') return (row.category?.name || '').toLowerCase()
    return row[sortKey.value]
  }
  list.sort((a, b) => {
    const va = getVal(a)
    const vb = getVal(b)
    const na = Number(va)
    const nb = Number(vb)
    if (Number.isFinite(na) && Number.isFinite(nb)) return (na - nb) * modifier
    const sa = String(va ?? '').toLowerCase()
    const sb = String(vb ?? '').toLowerCase()
    if (sa < sb) return -1 * modifier
    if (sa > sb) return 1 * modifier
    return 0
  })
  return list
})

function handlePrintReport() {
  if (!sortedRows.value.length) {
    alert('No data to print. Please generate the report first.')
    return
  }
  window.print()
}

onMounted(async () => {
  await Promise.all([loadCategories(), loadDateOptions()])
})
</script>

<style scoped>
table {
  font-family: 'Segoe UI', sans-serif;
  font-size: 11px;
  line-height: 1.15;
  color: #1e293b;
}

th, td {
  border-bottom: 1px solid #e2e8f0;
  white-space: nowrap;
  padding: 3px 4px;
  font-size: inherit;
}

th {
  background: #f1f5f9;
  cursor: default;
  font-weight: 600;
}

.saved-table-wrap {
  min-height: 70vh;
}

@media print {
  :global(.sidebar),
  :global(header),
  :global(nav),
  :global(.top-bar),
  :global(.app-header),
  :global(.logout-btn) {
    display: none !important;
  }

  .print-hide { display: none !important; }

  body, .bg-\[\#f8fafc\] {
    background: transparent !important;
  }

  .overflow-x-auto {
    overflow: visible !important;
  }

  .print-container {
    box-shadow: none !important;
    border: none !important;
    width: 100% !important;
    max-width: 100% !important;
    overflow: visible !important;
  }

  .print-table {
    width: 100% !important;
    border-collapse: collapse !important;
    border-spacing: 0 !important;
  }

  .print-table th, .print-table td {
    border: 0 !important;
    border-right: 1px solid #000 !important;
    border-bottom: 1px solid #000 !important;
    padding: 4px !important;
    text-align: left !important;
    color: #000 !important;
    font-size: 10px !important;
    white-space: normal !important;
    word-break: break-word !important;
  }

  .print-table tr > *:first-child {
    border-left: 1px solid #000 !important;
  }

  .print-table thead tr:first-child > * {
    border-top: 1px solid #000 !important;
  }

  .print-table th {
    background-color: #f3f3f3 !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .print-doc-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
  }

  .print-doc-header-grid {
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    align-items: center;
    column-gap: 12px;
  }

  .print-doc-left {
    justify-self: start;
  }

  .print-doc-title {
    justify-self: center;
    margin: 0 !important;
    white-space: nowrap;
  }

  .print-doc-right {
    justify-self: end;
  }

  .print-company-block {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;
  }

  .print-company-logo {
    width: 52px;
    height: 52px;
    min-width: 52px;
    min-height: 52px;
    max-width: 52px;
    max-height: 52px;
    flex: 0 0 52px;
    display: block;
    object-fit: contain;
    border: 0 !important;
    border-radius: 0;
    padding: 0;
    background: #fff;
  }

  .print-company-logo-fallback {
    width: 52px;
    height: 52px;
    min-width: 52px;
    min-height: 52px;
    max-width: 52px;
    max-height: 52px;
    flex: 0 0 52px;
    border: 1px solid #000;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 10px;
    color: #334155;
    background: #fff;
  }

  .print-company-text {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .print-company-name {
    font-size: 18px;
    font-weight: 700;
    color: #0f172a;
    line-height: 1.2;
  }

  .print-doc-meta {
    font-size: 12px;
    color: #0f172a;
    white-space: nowrap;
  }
}
</style>
